import React from 'react';
import Dialog from '../dialog.jsx';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import R from 'ramda';

// HACK: v2 api limitations shared_with still uses a v2 api, and I have no idea
// why but it uses the full protcol and host in its URIs
const hackUri = function(uri) {
    uri = uri.replace(/api\/v3/, 'api');
    return `${window.location.protocol}//${window.location.host}${uri}`;
};

export default class extends React.Component {
    static propTypes = {
        params: PropTypes.object.isRequired,
        routeToContentPage: PropTypes.func.isRequired,
        content: PropTypes.object,
        settings: PropTypes.object,
        students: PropTypes.array,
        saveSharing: PropTypes.func,
    };

    state = {
        mode: null,
        with_all_instructor: null,
        selected_students: {},
    };

    dialog = React.createRef();

    onClose = () => {
        const { routeToContentPage, params } = this.props;
        routeToContentPage(params);
    };

    onSave = () => {
        const { content, saveSharing } = this.props;
        const mergedState = this.getMergedState();
        let { shared_with } = mergedState;
        const { mode, with_all_instructor } = mergedState;

        // I think if we're sending the boolean we probably shouldn't send the
        // whole list. This probably opts them into sharing with students that
        // join the class later, but selecting every student would not.
        if (mode === 'all') {
            shared_with = [];
        }

        saveSharing(content, {
            with_all_instructor,
            with_all_students: mode === 'all',
            shared_with,
        });
        this.dialog.current.dismiss();
    };

    // This view may render initially with defaults and without a list of
    // students. If it saves those values in the state it won't update when the
    // real data arrives, but we also need to change the local state on demand.
    // My solution to this is for the state to be empty by default and
    // everything defaults to whatever has been passed in. But once you change a
    // value (like the mode) then we ignore what was passed in and just use the
    // state.
    getMergedState = () => {
        const { settings } = this.props;
        let { mode, with_all_instructor } = this.state;
        const { selected_students } = this.state;

        const gatherShared = R.compose(
            R.reduce((shared, [uri, selected]) => {
                if (selected) {
                    return R.append(uri, shared);
                }
                return R.difference(shared, [uri]);
            }, settings.shared_with),
            R.toPairs
        );
        let shared_with = gatherShared(selected_students);

        if (mode == null) {
            if (settings.with_all_students) {
                mode = 'all';
            } else {
                mode = shared_with.length > 0 ? 'some' : 'none';
            }
        }

        if (mode === 'all') {
            const tmp = R.compose(
                R.map(s => hackUri(s.resource_uri)),
                R.values
            );

            shared_with = tmp(this.props.students);
        } else if (mode === 'none') {
            shared_with = [];
        }

        if (with_all_instructor == null) {
            with_all_instructor = settings.with_all_instructor;
        }

        return {
            mode,
            with_all_instructor,
            shared_with,
        };
    };

    changeInstructorSharing = event => {
        this.setState({
            // In the UI you would check the box to *HIDE* your notes from the
            // instructor, so the checked value needs to be inverted.
            with_all_instructor: !event.target.checked,
        });
    };

    switchMode = mode => {
        this.setState({ mode });
    };

    toggleStudent = uri => {
        const { shared_with } = this.getMergedState();
        const selected = R.contains(uri, shared_with);
        const selected_students = {
            ...this.state.selected_students,
            [uri]: !selected,
        };

        this.setState({
            mode: 'some',
            selected_students,
        });
    };

    renderStudents = () => {
        const { students } = this.props;
        const { mode, shared_with } = this.getMergedState();
        return students.map((student, index) => {
            const id = 'student-' + index;
            const uri = hackUri(student.resource_uri);

            return (
                <li key={uri}>
                    <label htmlFor={id}>{student.full_name}</label>
                    <input
                        type="checkbox"
                        id={id}
                        name={id}
                        value={uri}
                        onChange={this.toggleStudent.bind(null, uri)}
                        disabled={mode !== 'some'}
                        checked={R.contains(uri, shared_with)}
                    />
                </li>
            );
        });
    };

    render() {
        const { content } = this.props;
        const { mode, with_all_instructor } = this.getMergedState();
        const buttons = [
            {
                label: 'Cancel',
                dismiss: true,
                onClick: this.onClose,
            },
            {
                label: 'Save Settings',
                primary: true,
                onClick: this.onSave,
            },
        ];

        return (
            <Dialog
                ref={this.dialog}
                title="Sharing Settings"
                buttons={buttons}
                onClose={this.onClose}
                className="sharing-settings"
            >
                <h4>{content.title}</h4>
                <p>{content.author_byline}</p>
                <fieldset>
                    <legend className="aural">
                        Hide notes from instructors
                    </legend>
                    <input
                        type="checkbox"
                        id="sharing_instructor"
                        name="sharing_instructor"
                        onChange={this.changeInstructorSharing}
                        checked={!with_all_instructor}
                    />
                    <label htmlFor="sharing_instructor">
                        Hide my notes from my instructors
                    </label>
                </fieldset>
                <fieldset className="sharing-settings-students">
                    <legend>
                        Share my notes and questions with other students:
                    </legend>
                    <ul className="settings-list">
                        <li>
                            <input
                                type="radio"
                                id="sharing_all"
                                name="sharing_students"
                                onChange={this.switchMode.bind(null, 'all')}
                                checked={mode === 'all'}
                            />
                            <label htmlFor="sharing_all">
                                All students in the class
                            </label>
                        </li>
                        <li>
                            <input
                                type="radio"
                                id="sharing_none"
                                name="sharing_students"
                                onChange={this.switchMode.bind(null, 'none')}
                                checked={mode === 'none'}
                            />
                            <label htmlFor="sharing_none">No students</label>
                        </li>
                        <li>
                            <input
                                type="radio"
                                id="sharing_subset"
                                name="sharing_students"
                                onChange={this.switchMode.bind(null, 'some')}
                                checked={mode === 'some'}
                            />
                            <label htmlFor="sharing_subset">
                                Only these students:
                            </label>
                        </li>
                    </ul>
                    <div
                        className={classnames('settings-students well', {
                            disabled: mode !== 'some',
                        })}
                    >
                        <ul className="students-list">
                            {this.renderStudents()}
                        </ul>
                    </div>
                </fieldset>
            </Dialog>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/engage_reader/sharing.ui.jsx